import { create } from "zustand";
import { persist } from "zustand/middleware";
import { Skin } from "@/enums/Skin";
import { PlaybackTrigger } from "@/enums/PlaybackTrigger";
import { Theme } from "@/enums/Theme";

interface UiStore {
  theme: Theme;
  skin: Skin;
  hasCaptions: boolean;
  /** Vocals still to come arrive as texts of their transcripts, with no audio. */
  isFastMode: boolean;
  isMuted: boolean;
  /** Mirrors the system preference and follows it while the game runs. */
  prefersReducedMotion: boolean;
  /** Development only: the prototype's two pacings, auto and click. */
  playbackTrigger: PlaybackTrigger;
  /** The player took over: no vocal starts itself until one has played through. */
  isAutoPlaySuspended: boolean;
  setTheme: (theme: Theme) => void;
  setSkin: (skin: Skin) => void;
  setCaptions: (hasCaptions: boolean) => void;
  setFastMode: (isFastMode: boolean) => void;
  setMuted: (isMuted: boolean) => void;
  setReducedMotion: (prefersReducedMotion: boolean) => void;
  setPlaybackTrigger: (playbackTrigger: PlaybackTrigger) => void;
  setAutoPlaySuspended: (isAutoPlaySuspended: boolean) => void;
}

/** Bumped when a stored shape stops being readable. Without a `migrate`, a bump discards the
 *  old preferences rather than reviving them half-parsed. */
const VERSION = 2;

/* Preferences only, and only the ones worth carrying between sessions. Muting is not one: a
   player arriving at a game they cannot hear has no way to know why, and on a shared machine
   it would be inherited from whoever sat there last. Fast mode is not one either: it turns
   every vocal into a text, so a player who tried it once would come back to a game with no
   voices and no way to know why. */
export function carriedPreferences(state: UiStore) {
  return {
    theme: state.theme,
    skin: state.skin,
    hasCaptions: state.hasCaptions,
  };
}

/** Version 1 stored fast mode. Dropped on read, so a player who left it on is not stuck. */
export function migratePreferences(stored: unknown, version: number): unknown {
  if (version < 2 && typeof stored === "object" && stored !== null) {
    const { isFastMode: _dropped, ...kept } = stored as Record<string, unknown>;

    return kept;
  }

  return stored;
}

export const useUiStore = create<UiStore>()(
  persist(
    (set) => ({
      theme: Theme.Dark,
      skin: Skin.Magenta,
      hasCaptions: true,
      isFastMode: false,
      isMuted: false,
      prefersReducedMotion: false,
      playbackTrigger: PlaybackTrigger.Auto,
      isAutoPlaySuspended: false,
      setTheme: (theme) => set({ theme }),
      setSkin: (skin) => set({ skin }),
      setCaptions: (hasCaptions) => set({ hasCaptions }),
      setFastMode: (isFastMode) => set({ isFastMode }),
      setMuted: (isMuted) => set({ isMuted }),
      setReducedMotion: (prefersReducedMotion) => set({ prefersReducedMotion }),
      setPlaybackTrigger: (playbackTrigger) => set({ playbackTrigger }),
      setAutoPlaySuspended: (isAutoPlaySuspended) => set({ isAutoPlaySuspended }),
    }),
    {
      name: "serenityrp.preferences",
      version: VERSION,
      partialize: carriedPreferences,
      migrate: migratePreferences,
    },
  ),
);
